// src/pages/Admin/AssignWork.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Form, Button, Spinner, Alert, OverlayTrigger, Popover, ProgressBar, Badge } from 'react-bootstrap';
import { FaSearch, FaCheck } from 'react-icons/fa';
import TitleWithBack from '../../components/common/TitleWithBack';
import PaginatedTable from '../../components/PaginatedTable';
import AssignmentModal from './AssignmentModal';
import { formatDate } from '../../utils/helpers';

export default function AssignWork() {
  const [allExams, setAllExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/exam-assignments/active/ready-to-assign', {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(res => {
        setAllExams(res.data || []);
        setFilteredExams(res.data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam assignments.');
        setLoading(false);
      });
  }, []);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearch = () => {
    if (!searchText.trim()) {
      setFilteredExams(allExams);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = allExams.filter(ex => {
      return (
        ex.moduleName?.toLowerCase().includes(lower) ||
        ex.examId?.toString().toLowerCase().includes(lower) ||
        ex.moduleCode?.toLowerCase().includes(lower)
      );
    });
    setFilteredExams(filtered);
  };

  const handleDeploy = (exam) => {
    setSelectedAssignment(exam);
  };

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'module', header: 'Module/Exam' },
    { key: 'code', header: 'Module Code' },
    { key: 'date', header: 'Exam Date' },
    { key: 'startTime', header: 'Start Time'},
    { key: 'durationMinutes', header: 'Duration (Min)' },
    { key: 'status', header: 'Status' },
  ];

  const renderCentresPopover = (exam) => {
    const statuses = exam.examCentreStatuses || [];
    const assignedCount = statuses.filter((c) => c.hasExaminers).length;
    return (
      <Popover id={`centres-popover-${exam.examId}`} style={{ maxWidth: '340px' }}>
        <Popover.Header as="h6">Centre Assignment Progress</Popover.Header>
        <Popover.Body style={{ maxHeight: '320px', overflowY: 'auto' }}>
          <div className="mb-2">
            <div className="d-flex justify-content-between small mb-1">
              <span>{assignedCount} of {statuses.length} centres assigned</span>
            </div>
            <ProgressBar
              now={statuses.length ? (assignedCount / statuses.length) * 100 : 0}
              variant={assignedCount === statuses.length && statuses.length > 0 ? 'success' : 'info'}
            />
          </div>
          {statuses.length === 0 ? (
            <div className="text-muted small">No exam centres found.</div>
          ) : (
            <ul className="list-unstyled mb-0">
              {statuses.map((centre) => (
                <li key={centre.examCentreId} className="mb-2 pb-2 border-bottom">
                  <div className="d-flex justify-content-between align-items-center">
                    <strong>{centre.examCentreName}</strong>
                    {centre.hasExaminers ? (
                      <Badge bg="success"><FaCheck className="me-1" />Assigned</Badge>
                    ) : (
                      <Badge bg="secondary">Unassigned</Badge>
                    )}
                  </div>
                  <div className="small text-muted">
                    {centre.registeredStudents} student{centre.registeredStudents === 1 ? '' : 's'}
                    {' · '}
                    {centre.assignedExaminersCount} examiner{centre.assignedExaminersCount === 1 ? '' : 's'} assigned
                  </div>
                  {centre.assignedExaminerNames && (
                    <div className="small">{centre.assignedExaminerNames}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Popover.Body>
      </Popover>
    );
  };

  const renderRow = (exam) => {
    return (
      <>
        <td>{exam.examId}</td>
        <td>{exam.moduleName}</td>
        <td>{exam.moduleCode}</td>
        <td>{formatDate(exam.examDate)}</td>
        <td>{exam.startTime}</td>
        <td>{exam.durationMinutes}</td>
        <td>
          {exam.assignedExamCentres} / {exam.totalExamCentres}
          <OverlayTrigger
            trigger="click"
            placement="bottom"
            overlay={renderCentresPopover(exam)}
            rootClose
          >
            <Button variant="outline-secondary" size="sm" className="ms-2">
              Centres
            </Button>
          </OverlayTrigger>

          <Button
            variant="primary"
            size="sm"
            className="ms-2"
            onClick={() => handleDeploy(exam)}
            disabled={exam.assignedExamCentres === exam.totalExamCentres}
          >
            Deploy
          </Button>
        </td>
      </>
    );
  };

  return (
    <Container fluid className="mt-2">
      <TitleWithBack title="Exam Assignments" />
      <Row className="mb-3">
        <Col md={3}>
          <Form.Label>Search by name or code</Form.Label>
          <Form.Control
            type="text"
            value={searchText}
            onChange={handleSearchChange}
          />
        </Col>
        <Col md="auto" className="d-flex align-items-end">
          <Button variant="primary" onClick={handleSearch}>
            <FaSearch className="me-2" />
            Search
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-3">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : filteredExams.length === 0 ? (
        <Alert variant="info">Nothing to show here</Alert>
      ) : (
        <PaginatedTable
          data={filteredExams}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}

      {selectedAssignment && (
        <AssignmentModal
          assignment={selectedAssignment}
          onClose={() => setSelectedAssignment(null)}
        />
      )}
    </Container>
  );
}
